import {
  Box,
  Typography,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Pagination,
  Button,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { saveAs } from "file-saver";

import { useTrueBizApi } from "../../api";
import { tzAwareHumanizeTimeString } from "../../utilities/formatting";
import { APIDataExport } from "../../types/APIDataExport";
import ExportMonitorsButton from "../Monitoring/MonitorsTable/ExportMonitorsButton";
import ExportSearchHistoryButton from "../SearchHistory/ExportSearchHistoryButton";

const RESULTS_PER_PAGE = 15;

export default function DataExports() {
  const api = useTrueBizApi();
  const [dataExports, setDataExports] = useState<APIDataExport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    const getDataExports = async () => {
      try {
        const records = await api.getDataExports();
        setDataExports(records);
      } catch (e) {
        console.error("Failed to fetch exports:", e);
      } finally {
        setLoading(false);
      }
    };
    getDataExports();
  }, [api]);

  const downloadFile = async (dataExport: APIDataExport) => {
    const filename = dataExport.filename || `export-${dataExport.id}.csv`;

    if (!dataExport.file_url) {
      return;
    }

    saveAs(dataExport.file_url, filename);
  };

  const getStateString = (exportState: string) => {
    switch (exportState) {
      case "ERROR":
        return "Error";
      case "SUCCESS":
        return "Success";
      default:
        return "Pending";
    }
  };

  const getExportTypeString = (exportType: string) => {
    if (exportType === "MonitoringAnomaliesCSV") {
      return "Monitored Companies";
    }

    if (exportType === "SearchHistoryCSV") {
      return "Search History";
    }

    Sentry.captureMessage(`unhandled data_export.export_type: ${exportType}`, {
      level: "error",
    });

    return exportType;
  };

  const startIndex = (page - 1) * RESULTS_PER_PAGE;
  const endIndex = startIndex + RESULTS_PER_PAGE;
  const pageRecords = dataExports.slice(startIndex, endIndex);

  return (
    <Box sx={{ pb: 3 }}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          Exports
          {loading && <CircularProgress size={28} sx={{ marginLeft: 1 }} />}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
          <ExportSearchHistoryButton linkText="Export Search History" />
          <ExportMonitorsButton linkText="Export Monitors" />
        </Box>
      </Box>
      <Paper elevation={2} sx={{ p: 0 }}>
        <TableContainer component={Box} sx={{ marginTop: 3 }}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Type</strong>
                </TableCell>

                <TableCell>
                  <strong>Created At</strong>
                </TableCell>

                <TableCell>
                  <strong>Status</strong>
                </TableCell>

                <TableCell>
                  <strong>Options</strong>
                </TableCell>

                <TableCell align="right">
                  <strong></strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {pageRecords.map((dataExport: APIDataExport) => (
                <TableRow key={dataExport.id}>
                  <TableCell component="th" scope="row">
                    {getExportTypeString(dataExport.export_type)}
                  </TableCell>

                  <TableCell>
                    {tzAwareHumanizeTimeString(dataExport.created_at)}
                  </TableCell>

                  <TableCell component="th" scope="row">
                    {getStateString(dataExport.export_state)}
                  </TableCell>

                  <TableCell component="th" scope="row">
                    {dataExport.export_options}
                  </TableCell>

                  <TableCell component="th" scope="row">
                    {dataExport.export_state === "SUCCESS" && (
                      <Button
                        variant="contained"
                        onClick={() => downloadFile(dataExport)}
                      >
                        Download
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {pageRecords.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>no exports found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {dataExports.length > RESULTS_PER_PAGE && (
        <Box
          display="flex"
          justifyContent="center"
          paddingTop={3}
          paddingBottom={2}
        >
          <Pagination
            siblingCount={3}
            count={Math.ceil(dataExports.length / RESULTS_PER_PAGE)}
            page={page}
            onChange={(_: React.ChangeEvent<unknown>, value: number) => {
              setPage(value);
            }}
          />
        </Box>
      )}
    </Box>
  );
}
